// Env-light install for the hair shader: the diffuse + specular samplers read
// the ENG-561 baked light-probe VOLUME (the SH grid LightProbeVolume installs
// as a THREE.LightProbe, exposed on window.__LIGHT_PROBE__) — the SAME source
// the ?debug_env=1 oracle ball shades from. No image cube, no PMREM, no
// reflection-capture pictures: an SH probe answers both queries because it's a
// band-limited radiance field.
//
// `self` is the HairRenderer instance: we read its sampler scale refs and write
// back `diffuseSampler` / `specularSampler` / `_sh27`.

import {
  makeSHDiffuseSampler,
  makeSHSpecularSampler,
  lightProbeToSH27,
  INV_PI,
} from '../../lib/envSamplers.js';

// Install the volume samplers. The closures read `self._sh27` live, so a later
// snapshotProbe() refresh changes the lighting without re-installing.
export function installVolumeEnv(self) {
  if (!self._sh27) self._sh27 = new Float32Array(27);
  if (!self.shCoeff48) self.shCoeff48 = new Float32Array(48);
  // Live-tunable scales, shared with the ?debug_env oracle via the same window
  // knobs so the hair and the oracle ball read identically.
  self._diffuseScale = self._diffuseScale || { value: INV_PI };
  self._specScale = self._specScale || { value: 1 };
  self.diffuseSampler = makeSHDiffuseSampler(self._sh27, self._diffuseScale);
  self.specularSampler = makeSHSpecularSampler(self._sh27, self._specScale);
}

// Default exposure-normalization target (DC luminance). A dim bake (dark
// office, DC≈0.07) and a bright one (studio, DC≈1.6) both get scaled so their
// ambient DC luminance lands here → the hair lights at a usable, consistent
// level regardless of how bright the env was baked.
const HAIR_SH_NORM_TARGET = 1.0;
const SH_NORM_EPS = 1e-4;

// Exposure-normalization factor for the env queries: target ÷ probe DC
// luminance. One scalar, so the light's COLOUR and directionality are
// preserved — only overall exposure changes. A genuinely dark probe (≈0) is
// left alone (factor 1) so "no light" stays black, never amplified noise;
// target ≤ 0 also returns 1 (normalization disabled). Pure — caller resolves
// `target` from window.__HAIR_SH_NORM__.
export function shExposureNorm(sh27, target = HAIR_SH_NORM_TARGET) {
  if (!(target > 0)) return 1;
  const lum = 0.2126 * sh27[0] + 0.7152 * sh27[1] + 0.0722 * sh27[2];
  if (lum < SH_NORM_EPS) return 1;
  return target / lum;
}

// Refresh `self._sh27` from the live light probe before a shade. Returns true
// if a probe was sampled. No probe yet (grid still loading / no-cache) → leave
// the last good SH in place; on the very first frame that's zeros → unlit hair,
// a visible "no lighting" state rather than faked grey.
//
// The SH stays RAW (drift tracking + the scale-invariant NeuralBSDF query read
// it unchanged); the exposure-normalization is folded into the diffuse +
// specular sampler scales, so only the env-light queries are re-exposed.
export function snapshotProbe(self) {
  const probe = (typeof window !== 'undefined') ? window.__LIGHT_PROBE__ : null;
  if (!probe) return false;
  lightProbeToSH27(probe, self._sh27);
  if (typeof window !== 'undefined') {
    const dS = +window.__ENV_DEBUG_DIFFUSE_SCALE__ || 1;
    const sS = +window.__ENV_DEBUG_SPEC_SCALE__ || 1;
    const t = window.__HAIR_SH_NORM__;
    const target = (t === undefined || t === null) ? HAIR_SH_NORM_TARGET : +t;
    const norm = shExposureNorm(self._sh27, target);
    self._diffuseScale.value = INV_PI * dS * norm;
    self._specScale.value = sS * norm;
  }
  return true;
}
